/**
 * Hook para Verificação Avançada de Status de Assinatura
 * 
 * DESCRIÇÃO:
 * Hook que monitora continuamente o status da assinatura da clínica,
 * incluindo verificações de vencimento, período de graça, bloqueios
 * e status de pagamento.
 * 
 * FUNCIONALIDADES:
 * - Verificação em tempo real do status da assinatura
 * - Detecção de período de graça (7 dias após vencimento)
 * - Identificação de problemas de pagamento
 * - Cálculo de dias restantes até vencimento/bloqueio
 * - Cache inteligente para otimizar performance
 * 
 * CONECTA COM:
 * - Tabela stripe_subscriptions
 * - Tabela clinicas
 * - Sistema de planos
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useClinica } from '@/contexts/ClinicaContext';

export interface SubscriptionStatus {
  // Status básico
  isActive: boolean;
  isExpired: boolean;
  isInGracePeriod: boolean;
  isBlocked: boolean;

  // Informações de pagamento
  hasPaymentIssues: boolean;
  paymentFailureCount: number;
  lastPaymentAttempt?: string;

  // Datas importantes
  currentPeriodEnd?: string;
  gracePeriodEnd?: string;
  daysUntilExpiration?: number;
  daysUntilBlocked?: number;

  // Plano atual
  planName: string;
  planDisplayName: string;
  stripeStatus?: string;

  // Indicadores de ação
  needsPayment: boolean;
  canRenew: boolean;
  showWarning: boolean;
  urgencyLevel: 'none' | 'low' | 'medium' | 'high' | 'critical';
}

export const useSubscriptionStatus = () => {
  // Verificar se o ClinicaContext está disponível
  let clinicaAtiva = null;
  try {
    const { useClinica } = require('@/contexts/ClinicaContext');
    const clinicaContext = useClinica();
    clinicaAtiva = clinicaContext?.clinicaAtiva;
  } catch (error) {
    console.log('🔍 [useSubscriptionStatus] ClinicaProvider não disponível ainda');
  }

  const { data: status, isLoading, error, refetch } = useQuery<SubscriptionStatus>({
    queryKey: ['subscription-status', clinicaAtiva?.id],
    queryFn: async (): Promise<SubscriptionStatus> => {
      if (!clinicaAtiva?.id) {
        // Retornar status default em vez de lançar erro
        console.log('🔍 [useSubscriptionStatus] Clínica não disponível, retornando status default');
        return {
          isActive: true,
          isExpired: false,
          isInGracePeriod: false,
          isBlocked: false,
          hasPaymentIssues: false,
          paymentFailureCount: 0,
          planName: 'free',
          planDisplayName: 'Gratuito',
          needsPayment: false,
          canRenew: false,
          showWarning: false,
          urgencyLevel: 'none'
        };
      }

      console.log('🔍 [useSubscriptionStatus] Verificando status da assinatura para clínica:', clinicaAtiva.id);

      // Buscar dados da assinatura Stripe
      const { data: stripeSubscription, error: stripeError } = await supabase
        .from('stripe_subscriptions')
        .select('*')
        .eq('clinica_id', clinicaAtiva.id)
        .single();

      // Buscar dados da clínica
      const { data: clinica, error: clinicaError } = await supabase
        .from('clinicas')
        .select('plano_atual, plano_expira_em')
        .eq('id', clinicaAtiva.id)
        .single();

      if (clinicaError) {
        throw clinicaError;
      }

      const now = new Date();
      const planName = clinica?.plano_atual || 'free';
      const expiresAt = clinica?.plano_expira_em ? new Date(clinica.plano_expira_em) : null;

      const planoNormalizado = String(planName).trim().toLowerCase();
      const planoManualComAcesso = ['basic', 'pro', 'premium', 'pmk'].includes(planoNormalizado);

      // Sem uma assinatura ativa, contas free e trials legados começam bloqueados.
      let status: SubscriptionStatus = {
        isActive: planoManualComAcesso,
        isExpired: !planoManualComAcesso,
        isInGracePeriod: false,
        isBlocked: !planoManualComAcesso,
        hasPaymentIssues: false,
        paymentFailureCount: 0,
        planName,
        planDisplayName: getPlanDisplayName(planName),
        needsPayment: false,
        canRenew: false,
        showWarning: false,
        urgencyLevel: 'none'
      };

      // Se tem assinatura Stripe, usar dados do Stripe
      if (stripeSubscription && !stripeError) {
        const currentPeriodEnd = stripeSubscription.current_period_end ? new Date(stripeSubscription.current_period_end) : null;
        const gracePeriodEnd = stripeSubscription.grace_period_end ? new Date(stripeSubscription.grace_period_end) : null;
        const paymentFailures = stripeSubscription.payment_failure_count || 0;

        status = {
          ...status,
          stripeStatus: stripeSubscription.status,
          currentPeriodEnd: currentPeriodEnd?.toISOString(),
          gracePeriodEnd: gracePeriodEnd?.toISOString(),
          paymentFailureCount: paymentFailures,
          lastPaymentAttempt: stripeSubscription.last_payment_attempt,
          hasPaymentIssues: paymentFailures > 0,

          // Calcular status baseado na assinatura Stripe
          isActive: stripeSubscription.status === 'active',
          isExpired: currentPeriodEnd ? now > currentPeriodEnd : false,
          isInGracePeriod: gracePeriodEnd ? now <= gracePeriodEnd && now > (currentPeriodEnd || now) : false,
        };

        // Calcular dias restantes
        if (currentPeriodEnd) {
          const diffTime = currentPeriodEnd.getTime() - now.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          status.daysUntilExpiration = Math.max(0, diffDays);
        }

        if (gracePeriodEnd) {
          const diffTime = gracePeriodEnd.getTime() - now.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          status.daysUntilBlocked = Math.max(0, diffDays);
        }

      } else if (expiresAt && planoManualComAcesso) {
        // Planos pagos manuais podem possuir vencimento administrado fora do Stripe.
        const diffTime = expiresAt.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        status.daysUntilExpiration = Math.max(0, diffDays);
        status.isExpired = now > expiresAt;
        status.isActive = !status.isExpired;
      }

      // Determinar se está bloqueado
      status.isBlocked = (status.isExpired && !status.isInGracePeriod) ||
        ['canceled', 'incomplete_expired'].includes(status.stripeStatus || '');

      // Determinar necessidade de pagamento
      status.needsPayment = status.hasPaymentIssues ||
        ['past_due', 'unpaid'].includes(status.stripeStatus || '');

      // Determinar se pode renovar
      status.canRenew = status.isExpired || status.needsPayment || status.isInGracePeriod;

      // Calcular nível de urgência e se deve mostrar aviso
      status.urgencyLevel = calculateUrgencyLevel(status);
      status.showWarning = status.urgencyLevel !== 'none';

      console.log('✅ [useSubscriptionStatus] Status calculado:', status);
      return status;
    },
    enabled: !!clinicaAtiva?.id,
    staleTime: 1000 * 30, // 30 segundos - verificação mais frequente para detectar mudanças rápido
    refetchInterval: 1000 * 60 * 5, // Recheck a cada 5 minutos
    refetchOnWindowFocus: true,
  });

  const queryClient = useQueryClient();

  // 🔄 REAL-TIME SUBSCRIPTION: Detectar mudanças de plano automaticamente
  useEffect(() => {
    if (!clinicaAtiva?.id) return;

    console.log('📡 [useSubscriptionStatus] Configurando Real-Time Subscription para mudanças de status');

    const channel = supabase
      .channel(`subscription-status-${clinicaAtiva.id}`)
      // Detectar mudanças na tabela clinicas (plano_atual, plano_expira_em)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'clinicas',
          filter: `id=eq.${clinicaAtiva.id}`
        },
        (payload) => {
          const newData = payload.new as any;
          console.log('📡 [useSubscriptionStatus] Clínica atualizada:', {
            plano_atual: newData.plano_atual,
            plano_expira_em: newData.plano_expira_em
          });
          queryClient.invalidateQueries({ queryKey: ['subscription-status', clinicaAtiva.id] });
        }
      )
      // Detectar mudanças na tabela stripe_subscriptions
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stripe_subscriptions',
          filter: `clinica_id=eq.${clinicaAtiva.id}`
        },
        (payload) => {
          console.log('📡 [useSubscriptionStatus] Stripe subscription atualizada:', payload);
          queryClient.invalidateQueries({ queryKey: ['subscription-status', clinicaAtiva.id] });
        }
      )
      .subscribe((status) => {
        console.log('📡 [useSubscriptionStatus] Status da subscription:', status);
      });

    return () => {
      console.log('🧹 [useSubscriptionStatus] Removendo Real-Time Subscription');
      supabase.removeChannel(channel);
    };
  }, [clinicaAtiva?.id, queryClient]);

  return {
    status: status || null,
    isLoading,
    error,
    refetch
  };
};

// Função auxiliar para obter nome de exibição do plano
const getPlanDisplayName = (planName: string): string => {
  const planNames: Record<string, string> = {
    free: 'Gratuito',
    basic: 'Básico',
    premium: 'Premium',
    professional: 'Profissional',
    pmk: 'PMK',
    trial: 'Pagamento pendente'
  };
  return planNames[planName] || planName;
};

// Função auxiliar para calcular nível de urgência
const calculateUrgencyLevel = (status: SubscriptionStatus): SubscriptionStatus['urgencyLevel'] => {
  // Bloqueado ou com problemas críticos
  if (status.isBlocked || status.paymentFailureCount >= 3) {
    return 'critical';
  }

  // Conta sem pagamento é crítica porque deve permanecer somente na página de cobrança.
  if (['free', 'trial'].includes(status.planName)) {
    return 'critical';
  }

  // Em período de graça
  if (status.isInGracePeriod) {
    return 'high';
  }

  // Problemas de pagamento
  if (status.hasPaymentIssues || status.needsPayment) {
    return 'high';
  }

  // Próximo ao vencimento (3 dias ou menos)
  if (status.daysUntilExpiration !== undefined && status.daysUntilExpiration <= 3) {
    return 'medium';
  }

  // Próximo ao vencimento (7 dias ou menos)
  if (status.daysUntilExpiration !== undefined && status.daysUntilExpiration <= 7) {
    return 'low';
  }

  return 'none';
};
